import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from './api';
import { enablePushForCurrentUser } from './capacitor';

const AuthContext = createContext();

// ─── Multi-session storage helpers ────────────────────────────────────────────
// Sessions live in localStorage under `bookvia-sessions` as an array of:
//   { id, type, label, avatar, token, user, business, manager }
// The "active" session is always mirrored in the legacy keys
// (`bookvia-token`, `bookvia-user`, `bookvia-business`, `bookvia-manager`)
// so the rest of the app can keep using them unchanged.
// ─────────────────────────────────────────────────────────────────────────────
const SESSIONS_KEY = 'bookvia-sessions';
const ACTIVE_SESSION_KEY = 'bookvia-active-session-id';

const readSessions = () => {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};

const writeSessions = (list) => {
  localStorage.setItem(SESSIONS_KEY, JSON.stringify(list));
};

const sessionIdFor = ({ type, user, business, manager }) => {
  if (type === 'manager') return `manager_${business?.id}_${manager?.worker_id}`;
  if (type === 'business_owner') return `business_${business?.id}`;
  return `client_${user?.id}`;
};

const applyActive = (session) => {
  localStorage.setItem('bookvia-token', session.token);
  localStorage.setItem('bookvia-user', JSON.stringify(session.user));
  if (session.business) {
    localStorage.setItem('bookvia-business', JSON.stringify(session.business));
  } else {
    localStorage.removeItem('bookvia-business');
  }
  if (session.manager) {
    localStorage.setItem('bookvia-manager', JSON.stringify(session.manager));
  } else {
    localStorage.removeItem('bookvia-manager');
  }
  localStorage.setItem(ACTIVE_SESSION_KEY, session.id);
};

const upsertSession = (session) => {
  const list = readSessions();
  const idx = list.findIndex(s => s.id === session.id);
  if (idx >= 0) list[idx] = session; else list.push(session);
  writeSessions(list);
  applyActive(session);
  return list;
};

// ─────────────────────────────────────────────────────────────────────────────

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessions, setSessions] = useState([]);

  const refreshSessionsState = useCallback(() => {
    setSessions(readSessions());
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('bookvia-token');
    const savedUser = localStorage.getItem('bookvia-user');
    const savedBusiness = localStorage.getItem('bookvia-business');

    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        if (savedBusiness) setBusiness(JSON.parse(savedBusiness));
        setIsAuthenticated(true);
        enablePushForCurrentUser().catch(() => {});
      } catch {
        // corrupt storage — clear everything
        localStorage.removeItem('bookvia-token');
        localStorage.removeItem('bookvia-user');
        localStorage.removeItem('bookvia-business');
        localStorage.removeItem('bookvia-manager');
      }
    }
    refreshSessionsState();
    setLoading(false);
  }, [refreshSessionsState]);

  // ─── Session lifecycle helpers ─────────────────────────────────────────────
  const commitSession = (session) => {
    upsertSession(session);
    setUser(session.user);
    setBusiness(session.business || null);
    setIsAuthenticated(true);
    refreshSessionsState();
    enablePushForCurrentUser().catch(() => {});
  };

  const switchSession = (sessionId) => {
    const list = readSessions();
    const target = list.find(s => s.id === sessionId);
    if (!target) return false;
    applyActive(target);
    setUser(target.user);
    setBusiness(target.business || null);
    setIsAuthenticated(true);
    refreshSessionsState();
    enablePushForCurrentUser().catch(() => {});
    return true;
  };

  const removeSession = (sessionId) => {
    const list = readSessions();
    const remaining = list.filter(s => s.id !== sessionId);
    writeSessions(remaining);
    const activeId = localStorage.getItem(ACTIVE_SESSION_KEY);
    if (activeId === sessionId) {
      if (remaining.length > 0) {
        applyActive(remaining[0]);
        setUser(remaining[0].user);
        setBusiness(remaining[0].business || null);
        setIsAuthenticated(true);
      } else {
        // no sessions left — full logout
        localStorage.removeItem('bookvia-token');
        localStorage.removeItem('bookvia-user');
        localStorage.removeItem('bookvia-business');
        localStorage.removeItem('bookvia-manager');
        localStorage.removeItem(ACTIVE_SESSION_KEY);
        setUser(null);
        setBusiness(null);
        setIsAuthenticated(false);
      }
    }
    refreshSessionsState();
  };

  // ─── Login flows ──────────────────────────────────────────────────────────
  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, user: userData } = response.data;
    const session = {
      id: sessionIdFor({ type: 'client', user: userData }),
      type: 'client',
      label: userData.full_name || email,
      avatar: userData.avatar_url || null,
      token,
      user: userData,
      business: null,
      manager: null,
    };
    commitSession(session);
    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    return response.data;
  };

  const buildOwnerSession = (token, businessData, ownerInfo, email) => {
    const userData = {
      role: 'business',
      email,
      business_id: businessData.id,
      is_manager: false,
      id: ownerInfo?.id || null,
      full_name: ownerInfo?.full_name || null,
      business_name: businessData.name,
      business_logo_url: businessData.logo_url || null,
    };
    return {
      id: sessionIdFor({ type: 'business_owner', business: businessData }),
      type: 'business_owner',
      label: businessData.name,
      avatar: businessData.logo_url || null,
      token,
      user: userData,
      business: businessData,
      manager: null,
    };
  };

  const businessLogin = async (email, password) => {
    const response = await authAPI.businessLogin({ email, password });
    const { token, business: businessData, owner: ownerInfo = {} } = response.data;
    const session = buildOwnerSession(token, businessData, ownerInfo, email);
    commitSession(session);
    return businessData;
  };

  const unifiedLogin = async (email, password) => {
    const response = await authAPI.unifiedLogin({ email, password });
    const { token, account_type, requires_admin_2fa } = response.data;

    // Admin/staff never get a token via unified-login — they must go through
    // /bv-ctrl/login to complete their 2FA step. We just surface a marker so
    // LoginPage can redirect them cleanly.
    if (requires_admin_2fa || account_type === 'admin') {
      return { account_type: 'admin', requires_admin_2fa: true, email };
    }

    if (account_type === 'business') {
      const businessData = response.data.business;
      const ownerInfo = response.data.owner || {};
      const session = buildOwnerSession(token, businessData, ownerInfo, email);
      commitSession(session);
      return { account_type: 'business', data: businessData };
    } else {
      const userData = response.data.user;
      const session = {
        id: sessionIdFor({ type: 'client', user: userData }),
        type: 'client',
        label: userData.full_name || email,
        avatar: userData.avatar_url || null,
        token,
        user: userData,
        business: null,
        manager: null,
      };
      commitSession(session);
      return { account_type: 'user', data: userData };
    }
  };

  const managerLogin = async (businessEmail, workerId, pin) => {
    const response = await authAPI.managerLogin({ business_email: businessEmail, worker_id: workerId, pin });
    const { token, business: businessData, manager } = response.data;
    const userData = {
      role: 'business',
      email: businessEmail,
      business_id: businessData.id,
      is_manager: true,
      manager_permissions: manager.permissions,
      worker_id: manager.worker_id,
      worker_name: manager.worker_name,
    };
    const session = {
      id: sessionIdFor({ type: 'manager', business: businessData, manager }),
      type: 'manager',
      label: `${manager.worker_name} · ${businessData.name}`,
      avatar: businessData.logo_url || null,
      token,
      user: userData,
      business: businessData,
      manager,
    };
    commitSession(session);
    return businessData;
  };

  const businessRegister = async (data) => {
    const response = await authAPI.businessRegister(data);
    return response.data;
  };

  const googleLogin = async (sessionId) => {
    const response = await authAPI.googleSession({ session_id: sessionId });
    const { token, user: userData } = response.data;
    const session = {
      id: sessionIdFor({ type: 'client', user: userData }),
      type: 'client',
      label: userData.full_name || userData.email,
      avatar: userData.avatar_url || null,
      token,
      user: userData,
      business: null,
      manager: null,
    };
    commitSession(session);
    return userData;
  };

  const appleLogin = async (payload) => {
    // payload: { identity_token, authorization_code?, nonce?, email?, given_name?, family_name? }
    const response = await authAPI.appleLogin(payload);
    const { token, user: userData } = response.data;
    const session = {
      id: sessionIdFor({ type: 'client', user: userData }),
      type: 'client',
      label: userData.full_name || userData.email,
      avatar: userData.avatar_url || null,
      token,
      user: userData,
      business: null,
      manager: null,
    };
    commitSession(session);
    return userData;
  };

  const adminLogin = async (email, password, totpCode) => {
    const response = await authAPI.adminLogin({ email, password, totp_code: totpCode });
    if (response.data.requires_2fa_setup) return response.data;
    const { token, user: userData } = response.data;
    const session = {
      id: sessionIdFor({ type: 'client', user: userData }),
      type: 'client',
      label: userData.full_name || userData.email,
      avatar: userData.avatar_url || null,
      token,
      user: userData,
      business: null,
      manager: null,
    };
    commitSession(session);
    return userData;
  };

  const logout = () => {
    // Log out ONLY the currently-active session. If more sessions exist,
    // switch to another one instead of dropping the user to the login screen.
    const activeId = localStorage.getItem(ACTIVE_SESSION_KEY);
    if (activeId) {
      removeSession(activeId);
    } else {
      localStorage.removeItem('bookvia-token');
      localStorage.removeItem('bookvia-user');
      localStorage.removeItem('bookvia-business');
      localStorage.removeItem('bookvia-manager');
      setUser(null);
      setBusiness(null);
      setIsAuthenticated(false);
      refreshSessionsState();
    }
  };

  const logoutAll = () => {
    writeSessions([]);
    localStorage.removeItem('bookvia-token');
    localStorage.removeItem('bookvia-user');
    localStorage.removeItem('bookvia-business');
    localStorage.removeItem('bookvia-manager');
    localStorage.removeItem(ACTIVE_SESSION_KEY);
    setUser(null);
    setBusiness(null);
    setIsAuthenticated(false);
    refreshSessionsState();
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
    const current = JSON.parse(localStorage.getItem('bookvia-user') || '{}');
    const merged = { ...current, ...userData };
    localStorage.setItem('bookvia-user', JSON.stringify(merged));

    // Also update the same field inside the active session record so it
    // survives account switching.
    const activeId = localStorage.getItem(ACTIVE_SESSION_KEY);
    if (activeId) {
      const list = readSessions();
      const idx = list.findIndex(s => s.id === activeId);
      if (idx >= 0) {
        list[idx] = { ...list[idx], user: merged };
        writeSessions(list);
        refreshSessionsState();
      }
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      updateUser(response.data);
      return response.data;
    } catch (error) {
      logout();
      throw error;
    }
  };

  const activeSessionId = localStorage.getItem(ACTIVE_SESSION_KEY);
  const isAdmin = user?.role === 'admin' || user?.role === 'staff';
  const isSuperAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff';
  const isBusiness = user?.role === 'business';
  const isUser = user?.role === 'user';
  const isManager = !!user?.is_manager;
  const managerPermissions = user?.manager_permissions || {};

  const hasPermission = (permission) => {
    if (!isManager) return true; // Owner has all permissions
    return !!managerPermissions[permission];
  };

  return (
    <AuthContext.Provider value={{
      user,
      business,
      loading,
      isAuthenticated,
      isAdmin,
      isSuperAdmin,
      isStaff,
      isBusiness,
      isUser,
      isManager,
      managerPermissions,
      hasPermission,
      login,
      register,
      businessLogin,
      unifiedLogin,
      managerLogin,
      businessRegister,
      adminLogin,
      googleLogin,
      appleLogin,
      logout,
      logoutAll,
      updateUser,
      refreshUser,
      // Multi-session
      sessions,
      activeSessionId,
      switchSession,
      removeSession,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
